import { useBottomSheetInternal } from '@gorhom/bottom-sheet'
import { BottomSheetInternalContextType } from '@gorhom/bottom-sheet/lib/typescript/contexts/internal'
import { useEffect, useRef, useState } from 'react'
import {
  TextInput as ActualTextInput,
  BlurEvent,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TextInputProps,
  TextInputSubmitEditingEventData,
  View,
  ViewStyle,
} from 'react-native'
import Animated, {
  Extrapolate,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import Outline from '@/components/form/Outline'
import UniBottomSheetTextInput from '@/components/unistyles/UniBottomSheetTextInput'

const BLUR_ANIMATION_DURATION = 180
const FOCUS_ANIMATION_DURATION = 150

const TextInput = (
  props: TextInputProps & {
    error?: boolean
    label?: string
    contentStyle?: StyleProp<ViewStyle>
    rightIcon?: React.ReactNode
    rightIconRight?: number
    rightIconTop?: number
  }
) => {
  const { theme } = useUnistyles()
  const inputRef = useRef<ActualTextInput>(null)

  const labelPositionY = useSharedValue(0)
  const labelPositionX = useSharedValue(0)

  const [focused, setFocused] = useState(false)

  // On web, submitBehavior='blurAndSubmit' (and the deprecated blurOnSubmit)
  // calls an API that doesn't exist in react-native-web (currentlyFocusedInput),
  // so we handle the blur manually via onSubmitEditing + ref.blur()
  const isWeb = Platform.OS === 'web'
  const { submitBehavior, blurOnSubmit, onSubmitEditing, ...restProps } = props

  const shouldBlurOnSubmit =
    submitBehavior === 'blurAndSubmit' || (!submitBehavior && blurOnSubmit)

  const resolvedSubmitBehavior =
    isWeb && shouldBlurOnSubmit ? 'submit' : (submitBehavior ?? undefined)

  const handleSubmitEditing = (
    e: NativeSyntheticEvent<TextInputSubmitEditingEventData>
  ) => {
    if (isWeb && shouldBlurOnSubmit) {
      inputRef.current?.blur()
    }
    onSubmitEditing?.(e)
  }

  const focusedAnimation = () => {
    labelPositionY.value = withTiming(-25, {
      duration: FOCUS_ANIMATION_DURATION,
    })
    labelPositionX.value = withTiming(-5, {
      duration: FOCUS_ANIMATION_DURATION,
    })
  }

  const onFocus = () => {
    setFocused(true)
    focusedAnimation()
  }

  useEffect(() => {
    if (props?.value?.length) {
      labelPositionY.value = -25
      labelPositionX.value = -5
    }
  }, [labelPositionX, labelPositionY, props?.value])

  const onBlur = (e: BlurEvent) => {
    setFocused(false)

    props.onBlur?.(e)

    if (props?.value?.length) {
      return
    }

    labelPositionY.value = withTiming(0, { duration: BLUR_ANIMATION_DURATION })
    labelPositionX.value = withTiming(0, { duration: BLUR_ANIMATION_DURATION })
  }

  const animatedLabelStyle = useAnimatedStyle(() => {
    return {
      zIndex: 1,
      transformOrigin: [14, 14, 0],
      transform: [
        {
          translateY: labelPositionY.value,
        },
        {
          translateX: labelPositionX.value,
        },
        {
          scale: interpolate(
            labelPositionY.value,
            [-20, 0],
            [0.75, 1],
            Extrapolate.CLAMP
          ),
        },
      ],
    }
  })

  const bottomSheetInternal: BottomSheetInternalContextType | null =
    useBottomSheetInternal(true)

  // On web, skip BottomSheetTextInput — its handleOnBlur calls
  // RNTextInput.State.currentlyFocusedInput() which doesn't exist in react-native-web.
  // Bottom sheet keyboard management is irrelevant on web anyway.
  const bottomSheet = isWeb ? null : bottomSheetInternal

  const labelColor = focused
    ? props.error
      ? theme.colors.error
      : theme.colors.primary
    : props.error
      ? theme.colors.error
      : theme.colors.onSurfaceVariant

  return (
    <View style={[styles.inputContainer, props.contentStyle]}>
      <Outline
        activeColor={props.error ? theme.colors.error : theme.colors.primary}
        hasActiveOutline={focused}
        label={!!props.label}
        outlineColor={props.error ? theme.colors.error : theme.colors.outline}
        roundness={4}
      />
      {props.label && (
        <Animated.View
          pointerEvents="none"
          style={[styles.labelContainer, animatedLabelStyle]}
        >
          <Animated.Text
            maxFontSizeMultiplier={1.5}
            numberOfLines={1}
            style={[
              styles.labelText(labelColor),
              props.rightIcon ? styles.textOverflow : undefined,
              props.editable === false && styles.disabled,
            ]}
          >
            {props.label}
          </Animated.Text>
        </Animated.View>
      )}
      {bottomSheet ? (
        <UniBottomSheetTextInput
          {...restProps}
          onBlur={onBlur}
          onFocus={onFocus}
          onSubmitEditing={handleSubmitEditing}
          style={[
            restProps.multiline && styles.multiline,
            restProps.style,
            styles.textInput,
            restProps.editable === false && styles.disabled,
          ]}
          submitBehavior={resolvedSubmitBehavior}
        />
      ) : (
        <ActualTextInput
          {...restProps}
          onBlur={onBlur}
          onFocus={onFocus}
          onSubmitEditing={handleSubmitEditing}
          ref={inputRef}
          style={[
            restProps.multiline && styles.multiline,
            restProps.style,
            styles.textInput,
            restProps.editable === false && styles.disabled,
          ]}
          submitBehavior={resolvedSubmitBehavior}
        />
      )}
      {props.rightIcon && (
        <View
          style={styles.rightIconContainer(
            props.rightIconRight,
            props.rightIconTop
          )}
        >
          {props.rightIcon}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create((theme, runtime) => {
  return {
    disabled: {
      color: theme.colors.onSurfaceDisabled,
    },
    inputContainer: {
      minHeight: 56,
      paddingTop: 6,
    },
    labelContainer: {
      backgroundColor: 'transparent',
      left: 5,
      position: 'absolute',
      top: 20,
    },
    labelText: (color: string) => ({
      backgroundColor: theme.colors.surface,
      color,
      fontSize: 16,
      paddingHorizontal: 8,
    }),
    multiline: {
      paddingTop: 10,
    },
    rightIconContainer: (rightIconRight?: number, rightIconTop?: number) => ({
      position: 'absolute' as const,
      right: rightIconRight ?? 38,
      top: rightIconTop ?? 20,
    }),
    textInput: {
      backgroundColor: 'transparent',
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: 0,
      color: theme.colors.onSurface,
      flex: Platform.OS === 'web' ? 1 : 0,
      fontSize: 16,
      justifyContent: 'center',
      minHeight: 48,
      paddingLeft: 12,
    },
    textOverflow: {
      marginRight: runtime.breakpoint === 'xs' ? 42 : 0,
      textOverflow: 'ellipsis',
    },
  }
})

export default TextInput
